import { css } from 'aphrodite';
import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Dropdown } from 'semantic-ui-react';
import { DropdownItemProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem';

import { validate } from '../../../application/security/auth';
import { USERS_WRITE_REQUIRED_PERMISSION } from '../../../application/security/permissions';
import { AppDispatch, RootState } from '../../../application/store';
import { User } from '../../../model/User';
import CommonModal from '../../common/modal/CommonModal';
import stylesCommons from '../../StylesCommons';
import { ActionType, performBatchAction } from '../reducer.BatchAction';
import { clearUsersById, unselectUsers } from '../reducer.Users';

interface BatchActionState {
    open: boolean;
    actionType: ActionType | 'EMPTY_ACTION';
}

interface BatchActionProps {
    callback: () => void;
}

const BatchActionDropdown: React.FC<BatchActionProps> = ({ callback }) => {
    const auth = useAuth();
    const [state, setState] = useState<BatchActionState>({
        open: false,
        actionType: 'EMPTY_ACTION',
    });
    const dispatch = useDispatch<AppDispatch>();
    const selectedUsers = useSelector(
        (state: RootState) => state.persistedReducers.users.userList.selectedUsers,
    );

    const open = (actionType: ActionType) => setState({ open: true, actionType: actionType });
    const close = () => setState({ open: false, actionType: 'EMPTY_ACTION' });
    const onItemClick = (event: React.MouseEvent<HTMLDivElement>, data: DropdownItemProps) =>
        open(data.value as ActionType);
    const performAction = async () => {
        const userIdp = selectedUsers.map((user) => user.idpId);
        try {
            await dispatch(
                performBatchAction({
                    actionType: state.actionType as ActionType,
                    administratorIds: userIdp,
                }),
            ).unwrap();
            close();
            dispatch(clearUsersById());
            dispatch(unselectUsers(selectedUsers));
            callback();
            toast.success(`Successful perform batch action: ${state.actionType}`);
        } catch (err) {
            toast.error(`Couldn't perform batch action: ${state.actionType}`);
        }
    };

    const createUserDetails = (user: User) => {
        if (user) {
            if (user.firstName && user.lastName) {
                return `${user.username} (${user.firstName} ${user.lastName})`;
            } else if (user.firstName) {
                return `${user.username} (${user.firstName})`;
            } else if (user.lastName) {
                return `${user.username} (${user.lastName})`;
            } else {
                return `${user.username}`;
            }
        }
        return '';
    };

    return (
        validate(USERS_WRITE_REQUIRED_PERMISSION, auth) && (
            <span>
                <Dropdown
                    className={css(stylesCommons.dropdown)}
                    disabled={selectedUsers.length === 0}
                    text={'Batch Actions'}
                    button>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            text={'Reset password'}
                            icon="key"
                            value={ActionType.RESET_PASSWORD}
                            onClick={onItemClick}
                        />
                        <Dropdown.Item
                            text={'Activate'}
                            icon="unlock"
                            value={ActionType.ACTIVATION}
                            onClick={onItemClick}
                        />
                        <Dropdown.Item
                            text={'Deactivate'}
                            icon="lock"
                            value={ActionType.DEACTIVATION}
                            onClick={onItemClick}
                        />
                    </Dropdown.Menu>
                </Dropdown>
                <CommonModal
                    isOpen={state.open}
                    header={`Confirm batch ${state.actionType} for users:`}
                    content={
                        <ul>
                            {selectedUsers.map((user) => (
                                <li key={user.idpId}>{createUserDetails(user)}</li>
                            ))}
                        </ul>
                    }
                    confirmButtonLabel={'Confirm'}
                    cancelButtonLabel={'Cancel'}
                    onConfirm={performAction}
                    onClose={close}
                />
            </span>
        )
    );
};

export default BatchActionDropdown;
